import React, { useState } from 'react';

import Icon from '../../components/AppIcon';
import NavigationHeader from '../homepage/components/NavigationHeader';
import Button from '../../components/ui/Button';
import AlertCard from './components/AlertCard';
import WeatherWidget from './components/WeatherWidget';
import EmergencyContactCard from './components/EmergencyContactCard';
import SafetyProtocolCard from './components/SafetyProtocolCard';
import AlertFilterBar from './components/AlertFilterBar';
import LiveStatusBanner from './components/LiveStatusBanner';
import EvacuationMapModal from './components/EvacuationMapModal';

const EmergencyAlertSystem = () => {
  const [filters, setFilters] = useState({
    severity: 'all',
    zone: 'all',
    type: 'all'
  });
  
  const [isEvacuationModalOpen, setIsEvacuationModalOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  const systemStatus = {
    level: 'warning',
    details: 'Tropical Storm Advisory - Heavy rainfall expected in next 6 hours',
    lastUpdated: '07 Dec 2025, 23:35'
  };

  const currentWeather = {
    temperature: 28,
    condition: 'rainy',
    humidity: 85,
    windSpeed: 45,
    visibility: 3.5,
    pressure: 1008,
    alerts: ['Heavy Rain Warning']
  };

  const activeAlerts = [
    {
      id: 1,
      severity: 'critical',
      title: 'Tropical Storm Warning',
      zone: 'All Zones',
      description: 'Tropical Storm Odette approaching from the east. Heavy rainfall and strong winds expected. All visitors must evacuate to designated safe zones immediately.',
      timestamp: '07 Dec 2025, 23:30',
      dismissible: false,
      affectedVisitors: 247,
      actions: ['Evacuate Now', 'Seek Shelter', 'Follow Ranger Instructions']
    },
    {
      id: 2,
      severity: 'high',
      title: 'Flash Flood Risk',
      zone: 'Underground River Trail',
      description: 'Rising water levels detected in underground river system. Trail access temporarily suspended. Visitors currently on trail must return immediately via marked emergency exits.',
      timestamp: '07 Dec 2025, 23:15',
      dismissible: false,
      affectedVisitors: 89,
      actions: ['Return to Surface', 'Use Emergency Exits', 'Contact Rangers']
    },
    {
      id: 3,
      severity: 'high',
      title: 'Wildlife Alert - Monitor Lizard',
      zone: 'Monkey Trail',
      description: 'Large monitor lizard (approximately 2 meters) spotted near Monkey Trail junction. Maintain safe distance of at least 10 meters. Do not approach or feed.',
      timestamp: '07 Dec 2025, 22:45',
      dismissible: true,
      affectedVisitors: 34,
      actions: ['Maintain Distance', 'Stay on Trail', 'Report Sightings']
    },
    {
      id: 4,
      severity: 'medium',
      title: 'Trail Closure Notice',
      zone: 'Jungle Trail Section B',
      description: 'Temporary trail closure due to fallen tree blocking path. Maintenance crew dispatched. Estimated reopening: 08 Dec 2025, 10:00. Alternative route via Section A available.',
      timestamp: '07 Dec 2025, 21:30',
      dismissible: true,
      affectedVisitors: 56,
      actions: ['Use Alternative Route', 'Check Trail Map']
    },
    {
      id: 5,
      severity: 'medium',
      title: 'Facility Maintenance',
      zone: 'Visitor Center',
      description: 'Restroom facilities at Visitor Center East Wing temporarily closed for emergency repairs. West Wing facilities remain operational.',
      timestamp: '07 Dec 2025, 20:15',
      dismissible: true,
      affectedVisitors: 0,
      actions: ['Use West Wing Facilities']
    },
    {
      id: 6,
      severity: 'low',
      title: 'Parking Capacity Alert',
      zone: 'Main Parking Area',
      description: 'Main parking area approaching 85% capacity. Overflow parking available at Secondary Lot B. Shuttle service operating every 15 minutes.',
      timestamp: '07 Dec 2025, 19:45',
      dismissible: true,
      affectedVisitors: 0,
      actions: ['Use Overflow Parking', 'Take Shuttle']
    }
  ];

  const emergencyContacts = [
    {
      id: 1,
      icon: 'Phone',
      title: 'Emergency Hotline',
      description: 'Immediate emergency response and ranger dispatch',
      phone: '+63 48 723 0909',
      email: 'emergency@ppsrnp.gov.ph',
      available24x7: true
    },
    {
      id: 2,
      icon: 'Ambulance',
      title: 'Medical Emergency',
      description: 'On-site medical assistance and ambulance services',
      phone: '+63 48 723 0910',
      email: 'medical@ppsrnp.gov.ph',
      available24x7: true
    },
    {
      id: 3,
      icon: 'Shield',
      title: 'Park Rangers',
      description: 'Wildlife encounters and visitor assistance',
      phone: '+63 48 723 0911',
      email: 'rangers@ppsrnp.gov.ph',
      available24x7: true
    },
    {
      id: 4,
      icon: 'MapPin',
      title: 'Visitor Information',
      description: 'General inquiries and non-emergency assistance',
      phone: '+63 48 723 0912',
      email: 'info@ppsrnp.gov.ph',
      available24x7: false
    }
  ];

  const safetyProtocols = [
    {
      id: 1,
      icon: 'CloudRain',
      title: 'Severe Weather Protocol',
      category: 'Weather Emergency',
      priority: 'high',
      description: 'Procedures to follow during tropical storms, heavy rainfall, or lightning events.',
      steps: [
        'Immediately seek shelter in designated safe zones (Visitor Center, Emergency Shelters)',
        'Stay away from open areas, tall trees, and water bodies',
        'Follow ranger instructions for evacuation if necessary',
        'Monitor weather updates via park radio or mobile app',
        'Do not attempt to return to vehicles during active storm',
        'Wait for all-clear signal from park management before resuming activities'
      ],
      emergencyContact: 'Emergency Hotline: +63 48 723 0909'
    },
    {
      id: 2,
      icon: 'Waves',
      title: 'Flash Flood Response',
      category: 'Water Emergency',
      priority: 'high',
      description: 'Actions to take when flash flooding occurs in underground river or trail areas.',
      steps: [
        'Move to higher ground immediately - do not wait for instructions',
        'Exit underground river system via nearest emergency exit',
        'Do not attempt to cross flowing water deeper than ankle level',
        'Abandon equipment if necessary to ensure personal safety',
        'Signal for help using whistle or flashlight if trapped',
        'Report to nearest ranger station once safe'
      ],
      emergencyContact: 'Emergency Hotline: +63 48 723 0909'
    },
    {
      id: 3,
      icon: 'Bug',
      title: 'Wildlife Encounter Protocol',
      category: 'Wildlife Safety',
      priority: 'medium',
      description: 'Safe behavior guidelines when encountering wildlife including monitor lizards, snakes, or macaques.',
      steps: [
        'Remain calm and do not make sudden movements',
        'Maintain minimum safe distance: 10 meters for monitor lizards, 5 meters for macaques',
        'Never feed, touch, or provoke wildlife',
        'Back away slowly while facing the animal',
        'Do not run or turn your back on large animals',
        'Report sighting to nearest ranger with location details',
        'Seek medical attention immediately if bitten or scratched'
      ],
      emergencyContact: 'Park Rangers: +63 48 723 0911'
    },
    {
      id: 4,
      icon: 'Heart',
      title: 'Medical Emergency Response',
      category: 'Health Emergency',
      priority: 'high',
      description: 'Steps for handling medical emergencies including injuries, heat exhaustion, or allergic reactions.',
      steps: [
        'Call emergency hotline immediately: +63 48 723 0910',
        'Provide exact location using trail markers or GPS coordinates',
        'Administer first aid if trained and safe to do so',
        'Keep patient calm and comfortable while awaiting help',
        'Do not move patient if spinal injury is suspected',
        'Gather patient medical information if available',
        'Clear area for emergency responder access'
      ],
      emergencyContact: 'Medical Emergency: +63 48 723 0910'
    },
    {
      id: 5,
      icon: 'Flame',
      title: 'Fire Emergency Protocol',
      category: 'Fire Safety',
      priority: 'high',
      description: 'Procedures for forest fire or facility fire emergencies.',
      steps: [
        'Alert others immediately by shouting "FIRE" and activating nearest alarm',
        'Evacuate area using nearest safe exit route',
        'Do not attempt to fight large fires - evacuate immediately',
        'Stay low to ground if smoke is present',
        'Close doors behind you to slow fire spread',
        'Report to designated assembly point for headcount',
        'Do not re-enter area until authorized by fire marshal'
      ],
      emergencyContact: 'Emergency Hotline: +63 48 723 0909'
    },
    {
      id: 6,
      icon: 'Users',
      title: 'Lost Person Protocol',
      category: 'Search & Rescue',
      priority: 'medium',
      description: 'Actions to take if you become separated from your group or lost on trails.',
      steps: [
        'Stay calm and stop moving to avoid getting more lost',
        'Use whistle to signal location (3 short blasts = distress signal)',
        'Stay on trail if possible - do not venture into unmarked areas',
        'Make yourself visible by staying in open areas',
        'Conserve phone battery and use only for emergency calls',
        'If night falls, find safe shelter and wait for rescue',
        'Answer calls from rangers - they may be searching for you'
      ],
      emergencyContact: 'Park Rangers: +63 48 723 0911'
    }
  ];

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      severity: 'all',
      zone: 'all',
      type: 'all'
    });
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismissing alert:', alertId);
  };

  const handleViewDetails = (alert) => {
    console.log('Viewing alert details:', alert);
  };

  const handleViewEvacuationMap = (zone) => {
    setSelectedZone(zone);
    setIsEvacuationModalOpen(true);
  };

  const filteredAlerts = activeAlerts?.filter(alert => {
    if (filters?.severity !== 'all' && alert?.severity !== filters?.severity) return false;
    if (filters?.zone !== 'all' && alert?.zone !== filters?.zone) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Emergency Banner */}
        <div className="bg-error text-error-foreground py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="AlertTriangle" size={20} className="pulse-urgent" />
              <span className="font-semibold">EMERGENCY ALERT ACTIVE</span>
              <span className="hidden md:inline">|</span>
              <span className="hidden md:inline">Tropical Storm Warning - All visitors must evacuate to safe zones</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="Phone"
              className="text-error-foreground hover:bg-error-foreground/10"
            >
              Call Emergency
            </Button>
          </div>
        </div>

        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-primary p-2 rounded-lg">
                  <Icon name="AlertCircle" size={28} className="text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-card-foreground font-bold text-2xl">Emergency Alert System</h1>
                  <p className="text-muted-foreground text-sm">Real-time Safety & Weather Monitoring</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="Bell"
                  onClick={() => console.log('Notification settings')}
                >
                  <span className="hidden md:inline">Notifications</span>
                </Button>
                <Button
                  variant="default"
                  iconName="Download"
                  onClick={() => console.log('Download offline resources')}
                >
                  <span className="hidden md:inline">Offline Resources</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8">
          <LiveStatusBanner status={systemStatus} />

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <button
              onClick={() => handleViewEvacuationMap('All Zones')}
              className="bg-card hover:bg-muted/5 p-6 rounded-lg shadow-conservation animation-organic text-left"
            >
              <Icon name="MapPin" size={32} className="text-error mb-3" />
              <h3 className="text-card-foreground font-semibold mb-1">Evacuation Routes</h3>
              <p className="text-muted-foreground text-sm">View emergency exits and assembly points</p>
            </button>

            <button
              onClick={() => window.location.href = 'tel:+63487230909'}
              className="bg-card hover:bg-muted/5 p-6 rounded-lg shadow-conservation animation-organic text-left"
            >
              <Icon name="Phone" size={32} className="text-success mb-3" />
              <h3 className="text-card-foreground font-semibold mb-1">Emergency Call</h3>
              <p className="text-muted-foreground text-sm">Direct line to emergency response</p>
            </button>

            <button
              onClick={() => setActiveTab('protocols')}
              className="bg-card hover:bg-muted/5 p-6 rounded-lg shadow-conservation animation-organic text-left"
            >
              <Icon name="FileText" size={32} className="text-info mb-3" />
              <h3 className="text-card-foreground font-semibold mb-1">Safety Protocols</h3>
              <p className="text-muted-foreground text-sm">Emergency response procedures</p>
            </button>

            <button
              onClick={() => console.log('Download offline map')}
              className="bg-card hover:bg-muted/5 p-6 rounded-lg shadow-conservation animation-organic text-left"
            >
              <Icon name="Download" size={32} className="text-warning mb-3" />
              <h3 className="text-card-foreground font-semibold mb-1">Offline Maps</h3>
              <p className="text-muted-foreground text-sm">Download for offline access</p>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6 border-b border-border">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-6 py-3 font-medium animation-organic ${
                activeTab === 'active' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Active Alerts ({filteredAlerts?.length})
            </button>
            <button
              onClick={() => setActiveTab('weather')}
              className={`px-6 py-3 font-medium animation-organic ${
                activeTab === 'weather' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Weather
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`px-6 py-3 font-medium animation-organic ${
                activeTab === 'contacts' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Emergency Contacts
            </button>
            <button
              onClick={() => setActiveTab('protocols')}
              className={`px-6 py-3 font-medium animation-organic ${
                activeTab === 'protocols' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Safety Protocols
            </button>
          </div>

          {/* Active Alerts Tab */}
          {activeTab === 'active' && (
            <div>
              <AlertFilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />

              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-foreground font-semibold text-xl">
                  Active Alerts ({filteredAlerts?.length})
                </h2>
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <div className="w-2 h-2 rounded-full bg-success pulse-conservation"></div>
                  <span>Live Updates</span>
                </div>
              </div>

              {filteredAlerts?.length > 0 ? (
                <div>
                  {filteredAlerts?.map(alert => (
                    <AlertCard
                      key={alert?.id}
                      alert={alert}
                      onDismiss={handleDismissAlert}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg p-12 text-center shadow-conservation">
                  <Icon name="CheckCircle" size={64} className="text-success mx-auto mb-4" />
                  <h3 className="text-card-foreground font-semibold text-xl mb-2">No Active Alerts</h3>
                  <p className="text-muted-foreground">All systems operational. Enjoy your visit safely.</p>
                </div>
              )}
            </div>
          )}

          {/* Weather Tab */}
          {activeTab === 'weather' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <WeatherWidget weather={currentWeather} />
                
                <div className="mt-6 bg-card rounded-lg p-6 shadow-conservation">
                  <h3 className="text-card-foreground font-semibold text-lg mb-4">7-Day Forecast</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                    {[
                      { day: 'Mon', temp: 29, condition: 'rainy' },
                      { day: 'Tue', temp: 27, condition: 'stormy' },
                      { day: 'Wed', temp: 28, condition: 'cloudy' },
                      { day: 'Thu', temp: 30, condition: 'sunny' },
                      { day: 'Fri', temp: 31, condition: 'sunny' },
                      { day: 'Sat', temp: 29, condition: 'cloudy' },
                      { day: 'Sun', temp: 28, condition: 'rainy' }
                    ]?.map((forecast, index) => (
                      <div key={index} className="bg-muted/10 rounded-lg p-4 text-center">
                        <div className="text-muted-foreground text-sm mb-2">{forecast?.day}</div>
                        <Icon name={forecast?.condition === 'sunny' ? 'Sun' : forecast?.condition === 'rainy' ? 'CloudRain' : forecast?.condition === 'stormy' ? 'CloudLightning' : 'Cloud'} size={32} className="text-accent mx-auto mb-2" />
                        <div className="text-card-foreground font-semibold">{forecast?.temp}°C</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-6 mb-6">
                  <div className="flex items-center gap-2 text-warning mb-3">
                    <Icon name="AlertTriangle" size={24} />
                    <h3 className="font-semibold text-lg">Active Weather Warnings</h3>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex gap-2">
                      <Icon name="ChevronRight" size={16} className="text-warning mt-1 flex-shrink-0" />
                      <div>
                        <div className="text-card-foreground font-medium">Tropical Storm Warning</div>
                        <div className="text-muted-foreground text-sm">Valid until 08 Dec, 18:00</div>
                      </div>
                    </li>
                    <li className="flex gap-2">
                      <Icon name="ChevronRight" size={16} className="text-warning mt-1 flex-shrink-0" />
                      <div>
                        <div className="text-card-foreground font-medium">Heavy Rainfall Advisory</div>
                        <div className="text-muted-foreground text-sm">Valid until 08 Dec, 12:00</div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-card rounded-lg p-6 shadow-conservation">
                  <h3 className="text-card-foreground font-semibold text-lg mb-4">Weather Safety Tips</h3>
                  <ul className="space-y-3 text-card-foreground">
                    <li className="flex gap-2">
                      <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Monitor weather updates regularly</span>
                    </li>
                    <li className="flex gap-2">
                      <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Carry rain gear and waterproof bags</span>
                    </li>
                    <li className="flex gap-2">
                      <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Avoid trails during heavy rain</span>
                    </li>
                    <li className="flex gap-2">
                      <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span>Follow ranger evacuation instructions</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Emergency Contacts Tab */}
          {activeTab === 'contacts' && (
            <div>
              <div className="mb-6">
                <h2 className="text-foreground font-semibold text-2xl mb-2">Emergency Contacts</h2>
                <p className="text-muted-foreground">24/7 emergency response and assistance services</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {emergencyContacts?.map(contact => (
                  <EmergencyContactCard key={contact?.id} contact={contact} />
                ))}
              </div>

              <div className="mt-8 bg-info/10 border border-info/20 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <Icon name="Info" size={24} className="text-info mt-1" />
                  <div>
                    <h3 className="text-card-foreground font-semibold text-lg mb-2">Important Information</h3>
                    <ul className="space-y-2 text-card-foreground">
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-info mt-1 flex-shrink-0" />
                        <span>All emergency numbers are toll-free and available 24/7</span>
                      </li>
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-info mt-1 flex-shrink-0" />
                        <span>Multilingual support available in English, Filipino, and Mandarin</span>
                      </li>
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-info mt-1 flex-shrink-0" />
                        <span>Emergency response time: Average 8-12 minutes within park boundaries</span>
                      </li>
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-info mt-1 flex-shrink-0" />
                        <span>Medical evacuation helicopter available for critical emergencies</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Safety Protocols Tab */}
          {activeTab === 'protocols' && (
            <div>
              <div className="mb-6">
                <h2 className="text-foreground font-semibold text-2xl mb-2">Safety Protocols</h2>
                <p className="text-muted-foreground">Emergency response procedures and safety guidelines</p>
              </div>

              <div className="space-y-4">
                {safetyProtocols?.map(protocol => (
                  <SafetyProtocolCard key={protocol?.id} protocol={protocol} />
                ))}
              </div>

              <div className="mt-8 bg-success/10 border border-success/20 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <Icon name="CheckCircle" size={24} className="text-success mt-1" />
                  <div>
                    <h3 className="text-card-foreground font-semibold text-lg mb-2">General Safety Reminders</h3>
                    <ul className="space-y-2 text-card-foreground">
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Always inform someone of your planned route and expected return time</span>
                      </li>
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Carry fully charged mobile phone and portable power bank</span>
                      </li>
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Stay on marked trails and follow all posted signage</span>
                      </li>
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Carry adequate water, snacks, and basic first aid supplies</span>
                      </li>
                      <li className="flex gap-2">
                        <Icon name="ChevronRight" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Download offline maps and emergency contact information before visiting</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border mt-16">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-card-foreground font-semibold mb-4">Emergency Resources</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="#" className="hover:text-card-foreground animation-organic">Evacuation Maps</a></li>
                  <li><a href="#" className="hover:text-card-foreground animation-organic">Safety Guidelines</a></li>
                  <li><a href="#" className="hover:text-card-foreground animation-organic">First Aid Stations</a></li>
                  <li><a href="#" className="hover:text-card-foreground animation-organic">Emergency Shelters</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-card-foreground font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li><a href="/visitor-planning-hub" className="hover:text-card-foreground animation-organic">Visitor Planning</a></li>
                  <li><a href="/interactive-conservation-map" className="hover:text-card-foreground animation-organic">Park Map</a></li>
                  <li><a href="/community-engagement-platform" className="hover:text-card-foreground animation-organic">Community</a></li>
                  <li><a href="/command-center-dashboard" className="hover:text-card-foreground animation-organic">Dashboard</a></li>
                </ul>
              </div>

              <div>
                <h3 className="text-card-foreground font-semibold mb-4">Contact Information</h3>
                <div className="space-y-3 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Phone" size={16} />
                    <span>+63 48 723 0909</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} />
                    <span>emergency@ppsrnp.gov.ph</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={16} />
                    <span>Puerto Princesa, Palawan</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-muted-foreground text-sm">
                © {new Date()?.getFullYear()} Puerto Princesa Subterranean River National Park. All rights reserved.
              </p>
              <div className="flex items-center gap-4 text-muted-foreground text-sm">
                <a href="#" className="hover:text-card-foreground animation-organic">Privacy Policy</a>
                <span>|</span>
                <a href="#" className="hover:text-card-foreground animation-organic">Terms of Service</a>
                <span>|</span>
                <a href="#" className="hover:text-card-foreground animation-organic">Accessibility</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <EvacuationMapModal
        isOpen={isEvacuationModalOpen}
        onClose={() => setIsEvacuationModalOpen(false)}
        zone={selectedZone}
      />
    </div>
  );
};

export default EmergencyAlertSystem;